import { addBreadcrumb, captureException } from '@sentry/browser'
import { AxiosError } from 'axios'
import { unwrap } from 'solid-js/store'
import { isServer } from 'solid-js/web'
import { useActionLogsSignal } from '../../../../rx/signal/useActionLogsSignal'
import { useAuthPlayerIdSignal } from '../../../../rx/signal/useAuthPlayerIdSignal'
import { useEnginePrevStateSignal } from '../../../../rx/signal/useEnginePrevStateSignal'
import { setAlertBagError } from '../../../../rx/store/AlertBag'
import { deepClone } from '../../../deep_clone'
import { getEngineAlertBag } from '../../../three/fn/getSetEngineAlertBag'
import { getEngineToast } from '../../../three/fn/getSetEngineToast'
import { saveToDisk } from '../../disk/DiskFactory'
import { Engine } from '../../engine/Engine.type'
import { ensureEngineWasmSession, resetEngineWasmSession } from '../../engine/engineWasm'
import { decodeStateSnapshotUnchecked, encodeActionMessage } from '../../engine/engineWasmCodec'
import { getEngineWasmCallErrorMessage, getEngineWasmSnapshotErrorMessage } from '../../engine/engineWasmErrors'
import { setEngineStateFromExternal, setEngineStateFromWasm } from '../../engine/setEngineState'
import { EntityTypeId } from '../../entity/EntityTypeId.enum'
import findEntityById from '../../entity/findEntityById'
import getEntitiesAtPosition from '../../entity/getEntitiesAtPosition'
import { StateMapDisk } from '../../state/state_map_disk.enum'
import { hasIndexedDB } from '../../Storage/IndexedDB/hasIndexedDB'
import type { TilePositionXY } from '../../tile_position_xy/TilePositionXY.type'
import toCoord from '../../tile_position_xy/toCoord'
import { byLayerIdDesc } from '../../util/id'
import Action from './action/Action.type'
import { ActionType } from './action/ActionType'
import type { MoveUnitAction } from './action/Game/MoveUnitAction'
import getActionTypeText from './action/getActionTypeText'
import type { ActionLog } from './ActionLog.type'
import type { ActionUnion } from './ActionUnion.type'
import nextClientReferenceId from './nextClientReferenceId'
import postRemoteAction from './postRemoteAction'

// let lastClientDispatch: Promise<unknown> | null = null
export default async function dispatchClient(engine: Engine, action: Action): Promise<void> {
  const [getAuthPlayerId] = useAuthPlayerIdSignal(engine)
  const [getActionLogs, setActionLogs] = useActionLogsSignal(engine)
  const [getPrevState, setPrevState] = useEnginePrevStateSignal(engine)
  console.log('%c[dispatchClient]', 'color: #BB6; font-weight: lighter; font-size: 10px;', getActionTypeText(action.type), {
    authPlayerId: getAuthPlayerId(),
    // players: engine.state.players.map((p) => ({
    //   player_id: p.id,
    //   profile_id: p.profile_id,
    // })),
    action: deepClone(action),
  })
  if (action.type === ActionType.Game.MoveUnit) {
    const moveUnitAction = action as MoveUnitAction
    const unit = findEntityById(engine.state.ents, moveUnitAction.unit_id)
    if (unit) {
      console.log('%c[dispatchClient][MoveUnit]', 'color: #BB6; font-weight: lighter; font-size: 10px;',
        EntityTypeId[unit.etype_id], toCoord(unit))
    }
  }

  addBreadcrumb({
    category: 'action',
    level: 'info',
    data: {
      action,
    },
  })
  if (isServer) {
    throw new Error('dispatchClient is only available on the client')
  }
  const { state: { round, ended } } = engine
  const authPlayerId = getAuthPlayerId()
  if (ended) {
    throw new Error('dispatchClient: game ended')
  }
  const actionLogs = getActionLogs()
  const originalActionLogsLen = actionLogs.length
  const prevState = deepClone(getPrevState())
  const actionLog: ActionLog = {
    cref: nextClientReferenceId(actionLogs, authPlayerId, round),
    round,
    player_id: authPlayerId,
    at: Date.now(),
    action: null as unknown as ActionUnion,
  }
  try {
    const session = await ensureEngineWasmSession(engine)
    const actionResult = session.instance.applyAction(session.handle, encodeActionMessage(action))
    const actionError = getEngineWasmCallErrorMessage(actionResult)
    if (actionError) {
      throw new Error(actionError)
    }
    const snapshot = session.instance.getFullState(session.handle)
    const snapshotError = getEngineWasmSnapshotErrorMessage(snapshot)
    if (snapshotError) {
      throw new Error(snapshotError)
    }

    const preferredLocalDisk = engine.state.map.disk === StateMapDisk.Memory ? StateMapDisk.Memory : StateMapDisk.IndexedDB
    const nextState = decodeStateSnapshotUnchecked(snapshot, preferredLocalDisk)
    setEngineStateFromWasm(engine, nextState)

    actionLog.action = action as ActionUnion

    const onlineId = engine.state.online?.id
    if (onlineId) {
      await postRemoteAction(engine, action, onlineId, actionLog)
    }

    setActionLogs([...actionLogs, actionLog])
    setPrevState(deepClone(nextState))

    await persistStateToIndexedDBIfNeeded(engine)

  } catch (err) {
    const toast = getEngineToast(engine)
    const ab = getEngineAlertBag(engine)
    const msg: string = (((err as AxiosError).response?.data as { message?: string })?.message)
      || (err as { message?: string }).message
      || 'unknown'
    const context: Record<string, unknown> = {
      action,
    }
    const err2 = new Error(`Action Failed: ${msg}`, { cause: err })
    console.error(err, context)
    captureException(err, context)
    if (toast) {
      toast.error(msg)
    }
    if (ab) {
      setAlertBagError(ab, err2)
    }

    // restore original state
    setEngineStateFromExternal(engine, prevState)
    setActionLogs(actionLogs.slice(0, originalActionLogsLen))
    await resetEngineWasmSession(engine, prevState)

    if (actionLog) {
      if (action.type === ActionType.Game.MoveUnit) {
        const moveUnitAction = action as MoveUnitAction
        const entIdKeys: Array<keyof MoveUnitAction> = [
          'unit_id',
          'target_id',
          'pickup_id',
          'taxi_id',
          'cargo_id',
          'merge_id',
        ] as const
        entIdKeys.forEach((key) => {
          if (moveUnitAction[key]) {
            const unit = findEntityById(engine.state.ents, moveUnitAction[key] as number)
            if (unit) {
              unit.hasError = true
            }
          }
        })
        const positionKeys: Array<keyof MoveUnitAction> = [
          'start_position',
          'dest_position',
          'attack_position',
          'unload_position',
        ] as const
        positionKeys.forEach((key) => {
          if (moveUnitAction[key]) {
            const entStack = getEntitiesAtPosition(engine.state.ents, moveUnitAction[key] as TilePositionXY)
            entStack.sort(byLayerIdDesc)
            if (entStack[0]) {
              entStack[0].hasError = true
            }
          }
        })
      }
    }
    throw err2
  }
}

async function persistStateToIndexedDBIfNeeded(engine: Engine): Promise<void> {
  if (engine.state.map.disk !== StateMapDisk.IndexedDB) {
    return
  }
  if (!hasIndexedDB()) {
    throw new Error('IndexedDB requested but not available')
  }
  await saveToDisk(unwrap(engine.state), StateMapDisk.IndexedDB)
}
